import {
  useMemo,
  useState,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import type { Study } from '@/models/study';
import { formatStudyDate } from '@/shared/utils/studyDate';
import { Tag } from '@/shared/ui/Tag';
import { formatTagLabel, tagSeverityRank } from '@/shared/utils/tagLabel';
import {
  beginStudyLoadGesture,
  getReportNavigationGuard,
} from '@/features/study-detail';
import styles from './WorklistRow.module.css';

const MAX_RAIL_TAGS = 2;

interface WorklistRowProps {
  study: Study;
  isActive: boolean;
  compact?: boolean;
}

export default function WorklistRow({
  study,
  isActive,
  compact = true,
}: WorklistRowProps) {
  const isStatPriority = study.priority === 'STAT';
  const isPending =
    study.assignedToMe && (study.reportStatus ?? '').toUpperCase() !== 'FINAL';
  const formattedDate = formatStudyDate(study.studyDate);
  // The rail is for picking the next study: no AI marks here (the studies
  // table and the Tags panel have them), most severe first, at most two tags. "+N" expands the
  // row to show them all; "Show less" folds it back.
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const tags = useMemo(
    () =>
      (study.tags ?? [])
        .map((tag) => ({ tag, rank: tagSeverityRank(tag) }))
        .sort((a, b) => b.rank - a.rank),
    [study.tags]
  );
  const hiddenTags = tags.slice(MAX_RAIL_TAGS);
  const shownTags = tagsExpanded ? tags : tags.slice(0, MAX_RAIL_TAGS);

  // The toggle sits inside the row's link: it must not open the study.
  const toggleTags = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTagsExpanded((expanded) => !expanded);
  };
  const handleToggleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') toggleTags(event);
  };

  return (
    <li aria-current={isActive ? 'true' : undefined}>
      <Link
        to={`/studies/${study.uuid}`}
        className={styles.row}
        aria-selected={isActive}
        aria-current={isActive ? 'true' : undefined}
        // Paint the loading overlay in THIS frame, before the router's
        // startTransition defers everything: flushSync commits the pending
        // flag synchronously, then the navigation (and the old study's
        // teardown) proceed behind the already-visible loader. Mirror Link's
        // own gating: only a plain left-click navigates in-page — a modified
        // or non-primary click opens a new tab and must NOT start the gesture
        // (this page never navigates, so nothing would ever clear it).
        onClick={(e) => {
          if (isActive) return;
          if (
            e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey
          )
            return;
          // Unsaved changes with autosave off: the router blocker is about
          // to show Leave/Stay — nothing may move (no overlay, highlight,
          // breadcrumb, URL) unless the user confirms (AP-778 §7.1).
          if (getReportNavigationGuard()?.blocksNavigation) return;
          const patientName =
            `${study.patientFirstName ?? ''} ${study.patientLastName ?? ''}`.trim();
          flushSync(() =>
            beginStudyLoadGesture(study.uuid, patientName || undefined)
          );
        }}
      >
        <span
          className={`${styles.pendingDot} ${isPending ? styles.pendingDotVisible : ''}`}
          aria-hidden
        />
        <span
          className={`${styles.priorityBar} ${isStatPriority ? styles.priorityStat : ''}`}
          aria-hidden
        />
        <div className={styles.content}>
          <div className={styles.line1}>
            <span className={styles.name}>
              {study.patientFirstName} {study.patientLastName}
            </span>
            {study.priority && (
              <span
                className={styles.priorityPill}
                data-priority={study.priority}
              >
                {{ STAT: '▲', URGENT: '●', ROUTINE: '▼' }[study.priority] ?? ''}
                {!compact && ` ${study.priority}`}
              </span>
            )}
          </div>
          <div className={styles.line2}>
            <span className={styles.site}>{study.site ?? ''}</span>
            <span className={styles.separator}>&middot;</span>
            <span className={styles.date}>{formattedDate}</span>
            {study.reportStatus && (
              <span
                className={styles.statusPill}
                data-status={(study.reportStatus ?? '').toUpperCase()}
              >
                {study.reportStatus}
              </span>
            )}
          </div>
          {tags.length > 0 && (
            <div className={styles.line3}>
              {shownTags.map(({ tag, rank }) => (
                <Tag
                  key={tag}
                  value={tag}
                  // CVv2 tags always use their short label here ("Severe AS").
                  compact={compact || rank > 0}
                />
              ))}
              {hiddenTags.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.tagsToggle}
                  aria-expanded={tagsExpanded}
                  onClick={toggleTags}
                  onKeyDown={handleToggleKeyDown}
                >
                  <Tag
                    value="MORE"
                    label={tagsExpanded ? 'Show less' : `+${hiddenTags.length}`}
                    title={
                      tagsExpanded
                        ? 'Show fewer tags'
                        : hiddenTags
                            .map(({ tag }) => formatTagLabel(tag))
                            .join(', ')
                    }
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}
